/**
 * Vendor contact facts from receipt text
 * ---------------------------------------
 * Pulls the OTHER party's letterhead block — address, city, state/province, postal
 * code, phone, fax, email, website, tax registration numbers — out of the raw text of
 * a receipt or invoice, so the free (browser) engine fills the vendor master
 * (site_mstr_global_busi_db) the same way the AI engine does.
 *
 * PRIVACY: public business facts only. Everything after the customer block
 * ("Sold to", "Bill to", "Ship to", "Customer", "Contact") is ignored, so a customer's
 * numbers are never read. A mobile / cell number in the vendor's OWN letterhead is kept
 * (many businesses publish one as a secondary official contact); a number labelled
 * "home" is not.
 *
 * Pure + unit-tested (vendorFacts.test.ts). Conservative: '' beats a wrong guess.
 */

export interface VendorFacts {
  address: string;
  city: string;
  state: string;        // code as printed, e.g. SK, NY
  postalCode: string;
  phone: string;
  fax: string;
  mobile: string;       // mobile / cell on the vendor's letterhead (secondary business line)
  email: string;
  website: string;
  taxId: string;
  taxIdLabel: string;   // GST, HST, PST, QST, VAT, EIN, ABN, BN …
  taxId2: string;
  taxId2Label: string;
}

const EMPTY: VendorFacts = {
  address: '', city: '', state: '', postalCode: '', phone: '', fax: '', mobile: '', email: '', website: '',
  taxId: '', taxIdLabel: '', taxId2: '', taxId2Label: '',
};

const REGIONS =
  'AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT|' +
  'AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC';
const CA_POSTAL = '[ABCEGHJ-NPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z][ -]?\\d[ABCEGHJ-NPRSTV-Z]\\d';
const PHONE = '(?:\\+?1[\\s.-]?)?\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]\\d{4}';
const STREET =
  '(?:st|street|ave|avenue|rd|road|hwy|highway|blvd|boulevard|dr|drive|way|lane|ln|cres|crescent|crt|court|ct|pl|place|trail|pkwy|parkway|sq|square|terr?|terrace)';

/** The part of the document that belongs to the vendor: everything before the customer block. */
export function vendorRegion(text: string): string {
  const t = String(text || '');
  const m = t.match(/\b(?:sold\s*to|bill(?:ed|ing)?\s*to|ship(?:ped|ping)?\s*to|deliver(?:ed|y)?\s*to|customer(?:\s*(?:name|id|no|#))?\s*:|contact\s*:|attn\.?\s*:|attention\s*:)/i);
  // A marker in the first few characters means the customer block leads the page — nothing
  // above it to read, so fall back to a bounded slice of the document.
  const cut = m && m.index != null && m.index > 30 ? t.slice(0, m.index) : t;
  return cut.slice(0, 1500);
}

const clean = (s: string) => s.replace(/\s+/g, ' ').replace(/^[\s,;:.-]+|[\s,;:-]+$/g, '').trim();

export function extractVendorFacts(text: string): VendorFacts {
  const region = vendorRegion(text);
  if (!region.trim()) return { ...EMPTY };
  const out: VendorFacts = { ...EMPTY };

  // ── Email / website ──
  const email = region.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/);
  if (email) out.email = email[0].toLowerCase();
  const noEmail = email ? region.replace(email[0], ' ') : region;
  const web = noEmail.match(/\b(?:https?:\/\/|www\.)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/[^\s,;]*)?|\b[A-Za-z0-9-]{3,}\.(?:com|ca|net|org|co|biz|info|io|us|uk|au)\b(?:\/[^\s,;]*)?/i);
  if (web) out.website = web[0].replace(/[.,;]+$/, '').toLowerCase();

  // ── Phone / fax / mobile (labelled numbers win; a "home" number is skipped) ──
  const phoneRe = new RegExp(`(?:\\b(phone|tel(?:ephone)?|ph|fax|facsimile|mobile|mob|cell(?:ular)?|home|toll[\\s-]*free|office|bus(?:iness)?)\\b\\s*(?:no\\.?|number|#)?\\s*[:.#-]?\\s*)?(${PHONE})`, 'gi');
  let pm: RegExpExecArray | null;
  let unlabelled = '';
  while ((pm = phoneRe.exec(region)) !== null) {
    const label = (pm[1] || '').toLowerCase();
    const num = clean(pm[2]);
    if (/^home/.test(label)) continue;                               // a person's number, not the business
    if (/^(mob|cell)/.test(label)) { if (!out.mobile) out.mobile = num; continue; }
    if (/^(fax|facsimile)/.test(label)) { if (!out.fax) out.fax = num; continue; }
    if (label) { if (!out.phone) out.phone = num; continue; }
    if (!unlabelled) unlabelled = num;
  }
  if (!out.phone && unlabelled && unlabelled !== out.fax && unlabelled !== out.mobile) out.phone = unlabelled;

  // ── City / state / postal code ──
  const caRe = new RegExp(`([A-Za-z][A-Za-z .'-]{1,40}?)[,.]?\\s+(${REGIONS})\\b[.,]?\\s+(${CA_POSTAL})`, 'i');
  const usRe = new RegExp(`([A-Za-z][A-Za-z .'-]{1,40}?)[,.]?\\s+(${REGIONS})\\b[.,]?\\s+(\\d{5}(?:-\\d{4})?)\\b`);
  const bareRe = new RegExp(`(?:^|\\n)[ \\t]*([A-Za-z][A-Za-z .'-]{1,40}?)[,.]?[ \\t]+(${REGIONS})\\.?[ \\t]*(?=\\n|$)`);
  const geo = region.match(caRe) || region.match(usRe) || region.match(bareRe);
  if (geo) {
    // The city is the last few words before the region code — drop anything dragged in
    // from earlier on a space-joined line (e.g. "… Hwy 16 W Foam Lake").
    let city = clean(geo[1].split('\n').pop() || '').split(' ').slice(-3).join(' ');
    const streetCut = city.match(new RegExp(`^.*\\b${STREET}\\b\\.?\\s+(?:\\d{1,4}\\s+)?(?:[NSEW]{1,2}\\b\\.?\\s+)?(.+)$`, 'i'));
    if (streetCut) city = streetCut[1];
    city = city.replace(/^(?:[NSEW]{1,2}\.?\s+)+/, '');            // a street direction left over from the line before
    out.city = clean(city);
    out.state = geo[2].toUpperCase();
    if (geo[3]) {
      const pc = geo[3].toUpperCase().replace(/[ -]/g, '');
      out.postalCode = /^[A-Z]/.test(pc) ? `${pc.slice(0, 3)} ${pc.slice(3)}` : geo[3];
    }
  } else {
    const pc = region.match(new RegExp(`\\b${CA_POSTAL}\\b`, 'i'));
    if (pc) { const v = pc[0].toUpperCase().replace(/[ -]/g, ''); out.postalCode = `${v.slice(0, 3)} ${v.slice(3)}`; }
  }

  // ── Street / PO box (the text that carries a box number or a civic number + street word) ──
  const addrRe = new RegExp(
    `(?:\\b(?:p\\.?\\s*o\\.?\\s*)?box\\s*\\d+[^\\n]{0,60})|(?:\\b\\d{1,6}[A-Za-z]?\\s+(?:[A-Za-z0-9.'#-]+\\s+){0,5}?${STREET}\\b\\.?(?:[ \\t]+\\d{1,4}\\b)?(?:[ \\t]+[NSEW]{1,2}\\b\\.?)?)`, 'i');
  const addr = region.match(addrRe);
  if (addr) {
    let a = addr[0];
    if (out.city) { const i = a.toLowerCase().lastIndexOf(out.city.toLowerCase()); if (i > 0) a = a.slice(0, i); }
    a = a.replace(/\b(?:phone|tel|fax|e-?mail|www)\b.*$/i, '');
    a = clean(a);
    if (/\.$/.test(addr[0].trim()) && /\b[NSEW]$/.test(a)) a += '.';   // keep "St. S." as printed
    out.address = a.slice(0, 255);
  }

  // ── Tax / business registration numbers (label kept as printed) ──
  const taxRe = /\b(GST\s*\/\s*HST|GST|HST|PST|QST|RST|VAT|EIN|ABN|BN|TIN)\b\s*(?:reg(?:istration|n)?\.?)?\s*(?:#|no\.?|number)?\s*[:#]?\s*(\d[\d-]{3,14}\d(?:\s?R[TCPMZ]\s?\d{4})?)/gi;
  let tm: RegExpExecArray | null;
  while ((tm = taxRe.exec(region)) !== null) {
    const label = tm[1].toUpperCase().replace(/\s+/g, '');
    const num = clean(tm[2]).toUpperCase();
    if (!out.taxId) { out.taxId = num; out.taxIdLabel = label; }
    else if (!out.taxId2 && num !== out.taxId) { out.taxId2 = num; out.taxId2Label = label; break; }
  }

  return out;
}
